import React, { useState } from 'react';
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { createAdditionalOrganization } from '../../features/auth/authSlice';

const COMPANY_SIZE_OPTIONS = [
  { value: '1', label: '1' },
  { value: '2-4', label: '2–4' },
  { value: '4-10', label: '4–10' },
  { value: '11-20', label: '11–20' },
  { value: '21-50', label: '21–50' },
  { value: '51-100', label: '51–100' },
  { value: '100+', label: '100+' },
];

const EMPTY_FORM = {
  name: '',
  company_size: '',
  address_street: '',
  address_city: '',
  address_state: '',
  address_postal_code: '',
  address_country: '',
};

const CreateOrganizationModal = ({ show, onHide, onCreated }) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const updateField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleClose = () => {
    if (submitting) return;
    setForm(EMPTY_FORM);
    setError('');
    setFieldErrors({});
    onHide();
  };

  const validate = () => {
    const nextErrors = {};
    if (!String(form.name || '').trim()) {
      nextErrors.name = 'Organization name is required.';
    }
    if (!String(form.company_size || '').trim()) {
      nextErrors.company_size = 'Company size is required.';
    }
    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setError('');
    try {
      await dispatch(
        createAdditionalOrganization({
          name: form.name.trim(),
          company_size: form.company_size,
          address_street: form.address_street.trim(),
          address_city: form.address_city.trim(),
          address_state: form.address_state.trim(),
          address_postal_code: form.address_postal_code.trim(),
          address_country: form.address_country.trim(),
        })
      ).unwrap();
      setForm(EMPTY_FORM);
      setFieldErrors({});
      onCreated?.();
      onHide();
    } catch (err) {
      if (err?.errors) {
        setFieldErrors(err.errors);
        setError(err.message || 'Please fix the highlighted fields.');
      } else {
        setError(
          typeof err === 'string'
            ? err
            : err?.message || 'Could not create organization.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>New organization</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <div className="alert alert-danger py-2">{error}</div>}
          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="new-org-name">
                <Form.Label>
                  Organization name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  value={form.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="Acme Corp"
                  required
                  isInvalid={Boolean(fieldErrors.name)}
                />
                <Form.Control.Feedback type="invalid">
                  {Array.isArray(fieldErrors.name) ? fieldErrors.name[0] : fieldErrors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="new-org-company-size">
                <Form.Label>
                  Company size <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={form.company_size}
                  onChange={(e) => updateField('company_size', e.target.value)}
                  required
                  isInvalid={Boolean(fieldErrors.company_size)}
                >
                  <option value="">Select company size</option>
                  {COMPANY_SIZE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {Array.isArray(fieldErrors.company_size)
                    ? fieldErrors.company_size[0]
                    : fieldErrors.company_size}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group controlId="new-org-address-street">
                <Form.Label>Street address</Form.Label>
                <Form.Control
                  value={form.address_street}
                  onChange={(e) => updateField('address_street', e.target.value)}
                  placeholder="123 Main St"
                  isInvalid={Boolean(fieldErrors.address_street)}
                />
                <Form.Control.Feedback type="invalid">
                  {Array.isArray(fieldErrors.address_street)
                    ? fieldErrors.address_street[0]
                    : fieldErrors.address_street}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="new-org-address-city">
                <Form.Label>City</Form.Label>
                <Form.Control
                  value={form.address_city}
                  onChange={(e) => updateField('address_city', e.target.value)}
                  placeholder="City"
                  isInvalid={Boolean(fieldErrors.address_city)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="new-org-address-state">
                <Form.Label>State</Form.Label>
                <Form.Control
                  value={form.address_state}
                  onChange={(e) => updateField('address_state', e.target.value)}
                  placeholder="State"
                  isInvalid={Boolean(fieldErrors.address_state)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="new-org-address-postal">
                <Form.Label>Postal code</Form.Label>
                <Form.Control
                  value={form.address_postal_code}
                  onChange={(e) => updateField('address_postal_code', e.target.value)}
                  placeholder="Postal code"
                  isInvalid={Boolean(fieldErrors.address_postal_code)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="new-org-address-country">
                <Form.Label>Country</Form.Label>
                <Form.Control
                  value={form.address_country}
                  onChange={(e) => updateField('address_country', e.target.value)}
                  placeholder="Country"
                  isInvalid={Boolean(fieldErrors.address_country)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Creating...
              </>
            ) : (
              'Create organization'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateOrganizationModal;
